import React from 'react';
import {Stack, styled} from '@mui/material';
import {Box} from '@mui/system';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
  Modal,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import styles from './LearnMoreModal.module.scss';

type LearnMoreModalProps = {
  isOpen: boolean;
  onClose: () => void;
  headline: string;
  image: string;
  items: {title: string; description: string}[];
};

const Container = styled(Stack)({
  padding: '40px',
  gap: '16px',
});

const Header = styled(Stack)({
  width: '100%',
  alignItems: 'flex-start',
  marginBottom: '8px',
});

function LearnMoreModal({
  isOpen,
  onClose,
  headline,
  image,
  items,
}: LearnMoreModalProps) {
  return (
    <Modal
      open={isOpen}
      handleClose={onClose}
      size="large"
      contentClassName={styles.modalContent}
    >
      <Container>
        <Header>
          <Headline size="xs">{translate(headline)}</Headline>
        </Header>
        <Box
          width="100%"
          height="100%"
          borderRadius="12px"
          component="img"
          src={image}
        />
        <Stack direction="column" gap="8px" marginBlockEnd="8px">
          {items.map((item) => (
            <GuideItem key={item.title} {...item} />
          ))}
        </Stack>
        <Button
          size="large"
          appearance="neutral"
          mode="filled"
          onClick={onClose}
          style={{width: '100%', height: '48px'}}
        >
          {translate('Got it')}
        </Button>
      </Container>
    </Modal>
  );
}

const GuideItemContainer = styled(Stack)(() => ({
  padding: '4px 12px',
  borderRadius: '8px',
  backgroundColor: designSystemToken('semantic.bg.tertiary'),
  paddingBlock: '8px',
}));

const GuideItemTitle = styled(Body)(() => ({
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '18px',
  letterSpacing: '-0.25px',
}));

const GuideItemDescription = styled(Body)(() => ({
  textAlign: 'start',
  fontSize: '12px',
  fontWeight: '500',
  lineHeight: '16px',
}));

function GuideItem({title, description}: {title: string; description: string}) {
  return (
    <GuideItemContainer direction="row" gap="8px">
      <Icon
        name="Actions-Accept-Small"
        size="medium"
        appearance="neutral"
        style={{
          width: '22px',
          height: '22px',
        }}
      />
      <Stack direction="column" gap="4px" alignItems="flex-start">
        <GuideItemTitle size="md">{translate(title)}</GuideItemTitle>
        <GuideItemDescription size="sm">
          {translate(description)}
        </GuideItemDescription>
      </Stack>
    </GuideItemContainer>
  );
}

export default LearnMoreModal;
